import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DateRangePicker,
    displayFormatTime,
    FormStore,
    InfiniteScroll,
    inject,
    NothingHere,
    SubTopBar,
    TasksStore,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { RouteComponentProps } from 'react-router';
import { Form } from 'reactstrap';
import { CloudConfigsStore } from '../../CloudConfigs/CloudConfigsStore';
import { AppStore } from '../../Core/AppStore';
import { DisplayEventsList } from '../DisplayEventsList';
import { DisplaysStore } from '../DisplaysStore';

export interface IDisplayConfigLogParams {
    serialNumber: string;
}
export interface IDisplayConfigLogProps extends RouteComponentProps<IDisplayConfigLogParams> {}

@autoinject
@observer
export class DisplayConfigLog extends React.Component<IDisplayConfigLogProps, any> {
    public constructor(props: IDisplayConfigLogProps) {
        super(props);
        this.state = {};

        const startTime = this.appStore.storage.displayLogTimes?.start
            ? new Date(this.appStore.storage.displayLogTimes?.start)
            : this.formStore.fields.startTime.value;
        if (!this.appStore.storage.displayLogTimes?.start) {
            startTime.setMonth(startTime.getMonth() - 1);
        } else {
            this.formStore.fields.startTime.setValue(startTime);
        }

        const endTime = this.appStore.storage.displayLogTimes?.end
            ? new Date(this.appStore.storage.displayLogTimes?.end)
            : this.formStore.fields.endTime.value;
        if (!this.appStore.storage.displayLogTimes?.end) {
            endTime.setDate(endTime.getDate() + 1);
            endTime.setMinutes(59);
            endTime.setSeconds(59);
        } else {
            this.formStore.fields.endTime.setValue(endTime);
        }
    }

    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getConfigLogAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>

                            <a className="btn btn-link" onClick={this.exportCsvAsync}>
                                Export to CSV
                            </a>
                        </div>
                    </Form>
                </SubTopBar>

                <InfiniteScroll
                    throttle={100}
                    threshold={300}
                    hasMore={this.hasMore}
                    onLoadMore={this.loadMore}
                    container={this.appStore.mainContainer}
                >
                    <table className="table table-striped fixed-header">
                        <thead>
                            <tr>
                                <th>Serial Number</th>
                                <th>Poll Time</th>
                                <th>Test Config</th>
                                <th>Local Config</th>
                                <th>Cloud Config</th>
                                <th>Legacy Config</th>
                                <th>Default Config</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.configs &&
                                this.configs.slice(0, this.scrollCount).map((c, idx) => (
                                    <tr key={idx}>
                                        <td>{c.displaySerialNumber}</td>
                                        <td>
                                            {displayFormatTime(
                                                c.pollTime,
                                                'EEEE, MMMM do yyyy, h:mm:ss a',
                                                this.props.match.params.serialNumber
                                            )}
                                        </td>
                                        <td>{c.testConfig}</td>
                                        <td>{c.localConfig}</td>
                                        <td>{c.cloudConfig}</td>
                                        <td>{c.legacyConfig}</td>
                                        <td>{c.defaultConfig}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </InfiniteScroll>

                <NothingHere
                    empty={!this.configs?.length}
                    icon={<BsTv />}
                    loading={this.loading}
                    label="No Display Events"
                    loadingMessage="Loading Events..."
                />
            </div>
        );
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getConfigLogAsync();
    }

    @bind
    @action
    private async getConfigLogAsync() {
        this.loading = true;
        this.appStore.storage.displayLogTimes = {
            start: this.formStore.fields.startTime.value,
            end: this.formStore.fields.endTime.value,
        };
        try {
            const configs = await this.displaysStore.getDisplayConfigLog(
                this.props.match.params.serialNumber,
                this.formStore.values.startTime,
                this.formStore.values.endTime
            );
            if (configs) this.configs = configs;
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async exportCsvAsync() {
        const query = stringify({
            startTime: this.formStore.values.startTime,
            endTime: this.formStore.values.endTime,
        });
        const task = this.tasksStore.startSessionTask('Export Display Config Log');

        try {
            await this.displaysStore.exportConfigLogAsync(this.props.match.params.serialNumber, query);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @bind
    @action
    private loadMore() {
        this.scrollCount += 40;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @computed
    private get hasMore() {
        if (this.configs) return this.scrollCount < this.configs.length;

        return false;
    }

    @observable
    private loading = false;
    @observable
    private configs?: Mri.DisplayConfigurations.DisplayConfigurationLog[];
    @observable
    private scrollCount = 40;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private cloudConfigsStore!: CloudConfigsStore;
    @inject
    private tasksStore!: TasksStore;
}
